import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Building2, Users, CheckCircle, Clock, CreditCard, ClipboardCheck, Coins, TrendingUp, Plus, UserPlus, Mail, Paintbrush } from 'lucide-react';
import { PageHeader } from '@/components/ui/PageHeader/PageHeader';
import { PageContainer } from '@/components/ui/PageContainer';
import { Button } from '../../components/ui/Button/Button';
import { Badge } from '../../components/ui/Badge/Badge';

interface DashboardStats {
  totalOrgs: number;
  totalUsers: number;
  activeOrgs: number;
  pendingInvites: number;
  activeSubscriptions: number;
  trialingSubscriptions: number;
  estimatedMrr: number;
  trialConversionRate: number;
}

interface RecentActivity {
  id: string;
  type: 'org_created' | 'user_registered' | 'invite_sent';
  description: string;
  timestamp: string;
}

export function AdminDashboard() {
  const [stats, setStats] = useState<DashboardStats>({
    totalOrgs: 0,
    totalUsers: 0,
    activeOrgs: 0,
    pendingInvites: 0,
    activeSubscriptions: 0,
    trialingSubscriptions: 0,
    estimatedMrr: 0,
    trialConversionRate: 0
  });
  const [recentActivity, setRecentActivity] = useState<RecentActivity[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        // Fetch dashboard stats and recent activity
        const [statsRes, activityRes] = await Promise.all([
          fetch('/api/admin/dashboard/stats', { credentials: 'include' }),
          fetch('/api/admin/dashboard/activity', { credentials: 'include' })
        ]);

        const statsData = await statsRes.json();
        const activityData = await activityRes.json();

        if (statsData.success) {
          setStats(statsData.data);
        }

        if (activityData.success) {
          // Format timestamps for display
          const formattedActivity = activityData.data.map((item: any) => ({
            ...item,
            timestamp: formatTimeAgo(new Date(item.timestamp))
          }));
          setRecentActivity(formattedActivity);
        }
      } catch (error) {
        console.error('Failed to load dashboard data:', error);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const formatTimeAgo = (date: Date) => {
    const now = new Date();
    const diffInMs = now.getTime() - date.getTime();
    const diffInMins = Math.floor(diffInMs / (1000 * 60));
    const diffInHours = Math.floor(diffInMs / (1000 * 60 * 60));
    const diffInDays = Math.floor(diffInMs / (1000 * 60 * 60 * 24));

    if (diffInMins < 60) {
      return `${diffInMins} minutes ago`;
    } else if (diffInHours < 24) {
      return `${diffInHours} hours ago`;
    } else {
      return `${diffInDays} days ago`;
    }
  };

  const formatCurrency = (value: number) =>
    new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 0 }).format(value);

  const formatPercentage = (value: number) => `${Math.round(value * 100)}%`;

  const statCards = [
    {
      title: 'Total Organizations',
      value: stats.totalOrgs,
      icon: <Building2 className="w-6 h-6" />,
      color: 'bg-primary-50 text-primary-600',
      href: '/admin/orgs'
    },
    {
      title: 'Total Users',
      value: stats.totalUsers,
      icon: <Users className="w-6 h-6" />,
      color: 'bg-secondary-50 text-secondary-600',
      href: '/admin/users'
    },
    {
      title: 'Active Organizations',
      value: stats.activeOrgs,
      icon: <CheckCircle className="w-6 h-6" />,
      color: 'bg-green-50 text-green-600',
      href: '/admin/orgs'
    },
    {
      title: 'Pending Invites',
      value: stats.pendingInvites,
      icon: <Clock className="w-6 h-6" />,
      color: 'bg-amber-50 text-amber-600',
      href: '/admin/invites'
    },
    {
      title: 'Active Subscriptions',
      value: stats.activeSubscriptions,
      icon: <CreditCard className="w-6 h-6" />,
      color: 'bg-indigo-50 text-indigo-600',
      href: '/admin/orgs'
    },
    {
      title: 'Trialing Organizations',
      value: stats.trialingSubscriptions,
      icon: <ClipboardCheck className="w-6 h-6" />,
      color: 'bg-blue-50 text-blue-600',
      href: '/admin/orgs'
    },
    {
      title: 'Estimated MRR',
      value: formatCurrency(stats.estimatedMrr),
      icon: <Coins className="w-6 h-6" />,
      color: 'bg-green-50 text-green-600',
      href: '/admin/billing'
    },
    {
      title: 'Trial → Paid Conversion',
      value: formatPercentage(stats.trialConversionRate),
      icon: <TrendingUp className="w-6 h-6" />,
      color: 'bg-purple-50 text-purple-600',
      href: '/admin/billing'
    }
  ];

  const getActivityIcon = (type: RecentActivity['type']) => {
    switch (type) {
      case 'org_created':
        return (
          <div className="w-8 h-8 bg-primary-100 rounded-full flex items-center justify-center">
            <Plus className="w-4 h-4 text-primary-600" />
          </div>
        );
      case 'user_registered':
        return (
          <div className="w-8 h-8 bg-green-100 rounded-full flex items-center justify-center">
            <UserPlus className="w-4 h-4 text-green-600" />
          </div>
        );
      case 'invite_sent':
        return (
          <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center">
            <Mail className="w-4 h-4 text-blue-600" />
          </div>
        );
    }
  };

  return (
    <PageContainer>
      <PageHeader
        title="Admin Dashboard"
        description="Welcome back! Here's an overview of your platform."
      />

      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-6">
        {statCards.map((card, index) => (
          <Link
            key={index}
            to={card.href}
            className="bg-white rounded-lg border border-gray-200 p-6 hover:shadow-md transition-shadow"
          >
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">{card.title}</p>
                <div className="text-2xl font-bold text-gray-900 mt-1">
                  {loading ? (
                    <div className="w-8 h-8 bg-gray-200 rounded animate-pulse" />
                  ) : (
                    card.value
                  )}
                </div>
              </div>
              <div className={`p-3 rounded-lg ${card.color}`}>
                {card.icon}
              </div>
            </div>
          </Link>
        ))}
      </div>

      {/* Content Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mt-6">
        {/* Recent Activity */}
        <div className="lg:col-span-2 bg-white rounded-lg border border-gray-200 p-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold text-gray-900">Recent Activity</h2>
            <Button variant="ghost" size="sm">
              View All
            </Button>
          </div>
          <div className="space-y-4">
            {recentActivity.map((activity) => (
              <div key={activity.id} className="flex items-start gap-3">
                {getActivityIcon(activity.type)}
                <div className="flex-1 min-w-0">
                  <p className="text-sm text-gray-900">{activity.description}</p>
                  <p className="text-xs text-gray-500 mt-1">{activity.timestamp}</p>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Quick Actions */}
        <div className="bg-white rounded-lg border border-gray-200 p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Quick Actions</h2>
          <div className="space-y-3">
            <Link to="/admin/orgs">
              <Button variant="outline" className="w-full justify-start">
                <Building2 className="w-4 h-4 mr-2" />
                Manage Organizations
              </Button>
            </Link>
            <Link to="/admin/users">
              <Button variant="outline" className="w-full justify-start">
                <Users className="w-4 h-4 mr-2" />
                View All Users
              </Button>
            </Link>
            <Link to="/admin/styleguide">
              <Button variant="outline" className="w-full justify-start">
                <Paintbrush className="w-4 h-4 mr-2" />
                Design System
              </Button>
            </Link>
          </div>
        </div>
      </div>

      {/* System Health */}
      <div className="bg-white rounded-lg border border-gray-200 p-6 mt-6">
        <h2 className="text-lg font-semibold text-gray-900 mb-4">System Health</h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="flex items-center gap-3">
            <div className="w-3 h-3 bg-green-500 rounded-full"></div>
            <div>
              <p className="text-sm font-medium text-gray-900">API Status</p>
              <p className="text-xs text-gray-500">All systems operational</p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <div className="w-3 h-3 bg-green-500 rounded-full"></div>
            <div>
              <p className="text-sm font-medium text-gray-900">Database</p>
              <p className="text-xs text-gray-500">Connected and healthy</p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <div className="w-3 h-3 bg-green-500 rounded-full"></div>
            <div>
              <p className="text-sm font-medium text-gray-900">Email Service</p>
              <p className="text-xs text-gray-500">Brevo integration active</p>
            </div>
          </div>
        </div>
      </div>
    </PageContainer>
  );
}
